import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { MessageSquare, Users, Info, ChevronDown, ChevronUp, Lock } from 'lucide-react';

interface ForumGuidelinesProps {
  className?: string;
}

const ForumGuidelines: React.FC<ForumGuidelinesProps> = ({ className }) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpanded = () => setIsExpanded(prev => !prev);
  
  return (
    <div className={cn(
      "rounded-xl border overflow-hidden",
      isDarkMode ? "bg-dark-200 border-dark-300" : "bg-light-200 border-light-300",
      className
    )}>
      <div 
        className={cn(
          "p-4 flex items-center justify-between cursor-pointer",
          isExpanded && "border-b",
          isDarkMode ? "border-dark-300 bg-dark-300/50" : "border-light-300 bg-light-300/50"
        )}
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <img 
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png" 
            alt="playclub Logo" 
            className="h-5 w-5" 
          />
          <h3 className={cn(
            "text-lg font-medium",
            isDarkMode ? "text-white" : "text-light-900 font-bold"
          )}>Community Guidelines</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className={cn(
            "h-5 w-5",
            isDarkMode ? "text-dark-800" : "text-light-600"
          )} />
        ) : (
          <ChevronDown className={cn(
            "h-5 w-5",
            isDarkMode ? "text-dark-800" : "text-light-600"
          )} />
        )}
      </div>
      
      {isExpanded && (
        <div className="p-5 space-y-4">
          <p className={cn(
            isDarkMode ? "text-dark-800" : "text-light-700 font-medium"
          )}>
            Welcome to the playclub forums! This is a space for members to connect, share ideas, and engage in meaningful conversations.
          </p>
          
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-dark-300 flex items-center justify-center">
                <MessageSquare className="h-3.5 w-3.5 text-gold-400" />
              </div>
              <div>
                <h4 className={cn(
                  "text-sm font-medium",
                  isDarkMode ? "text-white" : "text-light-900 font-semibold"
                )}>Express Yourself Freely</h4>
                <p className={cn(
                  "text-sm",
                  isDarkMode ? "text-dark-800" : "text-light-700"
                )}>Share your thoughts, experiences, and perspectives openly. We value diverse viewpoints and authentic expression.</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-dark-300 flex items-center justify-center">
                <Users className="h-3.5 w-3.5 text-gold-400" />
              </div>
              <div>
                <h4 className={cn(
                  "text-sm font-medium",
                  isDarkMode ? "text-white" : "text-light-900 font-semibold"
                )}>Respect the Community</h4>
                <p className={cn(
                  "text-sm",
                  isDarkMode ? "text-dark-800" : "text-light-700"
                )}>Treat others with respect and courtesy. Avoid personal attacks, harassment, or discriminatory language.</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-dark-300 flex items-center justify-center">
                <Lock className="h-3.5 w-3.5 text-gold-400" />
              </div>
              <div>
                <h4 className={cn(
                  "text-sm font-medium",
                  isDarkMode ? "text-white" : "text-light-900 font-semibold"
                )}>Keep Content Private</h4>
                <p className={cn(
                  "text-sm",
                  isDarkMode ? "text-dark-800" : "text-light-700"
                )}>Content shared within playclub should remain within our community. Do not share or repost content outside the platform.</p>
              </div>
            </div>
          </div>
          
          <div className={cn(
            "p-3 rounded-lg flex items-start gap-2 text-sm",
            isDarkMode ? "bg-dark-300" : "bg-light-300"
          )}>
            <Info className="h-4 w-4 text-gold-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className={cn(
                isDarkMode ? "text-dark-800" : "text-light-700 font-medium"
              )}>
                Content that promotes illegal activities, violence, or goes against the spirit of our community will be removed. Repeated violations may result in account restrictions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumGuidelines;